/**
 * ScreenshotAnnotator -- Canvas overlay for annotating bug report screenshots.
 *
 * Three tools: Freehand (red pen), Arrow (click-drag), Text (click to place).
 * All drawings in red (#FF0000), 2px stroke. Actions stored as array for undo.
 * Uses HTML5 Canvas API only -- no external dependencies.
 */

import { useState, useRef, useCallback, useEffect } from 'react'
import { Pencil, ArrowRight, Type, Undo2, Trash2, Check, X } from 'lucide-react'
import { cn } from '@/shared/utils/cn'

// ============================================================================
// Types
// ============================================================================

type AnnotationTool = 'freehand' | 'arrow' | 'text'

interface FreehandAction {
  type: 'freehand'
  points: Array<{ x: number; y: number }>
}

interface ArrowAction {
  type: 'arrow'
  start: { x: number; y: number }
  end: { x: number; y: number }
}

interface TextAction {
  type: 'text'
  position: { x: number; y: number }
  content: string
}

type AnnotationAction = FreehandAction | ArrowAction | TextAction

interface ScreenshotAnnotatorProps {
  imageUrl: string
  onSave: (annotatedDataUrl: string) => void
  onCancel: () => void
}

// ============================================================================
// Constants
// ============================================================================

const STROKE_COLOR = '#FF0000'
const STROKE_WIDTH = 2
const ARROWHEAD_LENGTH = 12
const FONT_SIZE = 14

// ============================================================================
// Drawing helpers
// ============================================================================

function drawFreehand(ctx: CanvasRenderingContext2D, points: Array<{ x: number; y: number }>) {
  if (points.length < 2) return
  ctx.beginPath()
  ctx.strokeStyle = STROKE_COLOR
  ctx.lineWidth = STROKE_WIDTH
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.moveTo(points[0].x, points[0].y)
  for (let i = 1; i < points.length; i++) {
    ctx.lineTo(points[i].x, points[i].y)
  }
  ctx.stroke()
}

function drawArrow(
  ctx: CanvasRenderingContext2D,
  start: { x: number; y: number },
  end: { x: number; y: number },
) {
  const dx = end.x - start.x
  const dy = end.y - start.y
  const angle = Math.atan2(dy, dx)

  ctx.beginPath()
  ctx.strokeStyle = STROKE_COLOR
  ctx.lineWidth = STROKE_WIDTH
  ctx.lineCap = 'round'
  ctx.moveTo(start.x, start.y)
  ctx.lineTo(end.x, end.y)
  ctx.stroke()

  // Arrowhead
  ctx.beginPath()
  ctx.fillStyle = STROKE_COLOR
  ctx.moveTo(end.x, end.y)
  ctx.lineTo(
    end.x - ARROWHEAD_LENGTH * Math.cos(angle - Math.PI / 6),
    end.y - ARROWHEAD_LENGTH * Math.sin(angle - Math.PI / 6),
  )
  ctx.lineTo(
    end.x - ARROWHEAD_LENGTH * Math.cos(angle + Math.PI / 6),
    end.y - ARROWHEAD_LENGTH * Math.sin(angle + Math.PI / 6),
  )
  ctx.closePath()
  ctx.fill()
}

function drawText(ctx: CanvasRenderingContext2D, position: { x: number; y: number }, content: string) {
  ctx.font = `${FONT_SIZE}px sans-serif`
  ctx.fillStyle = STROKE_COLOR
  ctx.fillText(content, position.x, position.y)
}

function drawAction(ctx: CanvasRenderingContext2D, action: AnnotationAction) {
  switch (action.type) {
    case 'freehand':
      drawFreehand(ctx, action.points)
      break
    case 'arrow':
      drawArrow(ctx, action.start, action.end)
      break
    case 'text':
      drawText(ctx, action.position, action.content)
      break
  }
}

// ============================================================================
// Component
// ============================================================================

export function ScreenshotAnnotator({ imageUrl, onSave, onCancel }: ScreenshotAnnotatorProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const imageRef = useRef<HTMLImageElement | null>(null)

  const [activeTool, setActiveTool] = useState<AnnotationTool>('freehand')
  const [actions, setActions] = useState<AnnotationAction[]>([])
  const [isDrawing, setIsDrawing] = useState(false)
  const [currentPoints, setCurrentPoints] = useState<Array<{ x: number; y: number }>>([])
  const [arrowStart, setArrowStart] = useState<{ x: number; y: number } | null>(null)
  const [arrowPreview, setArrowPreview] = useState<{ x: number; y: number } | null>(null)
  const [textInput, setTextInput] = useState<{ x: number; y: number; value: string } | null>(null)
  const [imageLoaded, setImageLoaded] = useState(false)

  // Load image and size canvas
  useEffect(() => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      imageRef.current = img
      setImageLoaded(true)
    }
    img.src = imageUrl
  }, [imageUrl])

  // Redraw canvas whenever actions or preview state change
  const redraw = useCallback(() => {
    const canvas = canvasRef.current
    const img = imageRef.current
    if (!canvas || !img) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    canvas.width = img.naturalWidth
    canvas.height = img.naturalHeight

    ctx.drawImage(img, 0, 0)

    for (const action of actions) {
      drawAction(ctx, action)
    }

    // Draw in-progress freehand
    if (isDrawing && activeTool === 'freehand' && currentPoints.length > 1) {
      drawFreehand(ctx, currentPoints)
    }

    // Draw in-progress arrow preview
    if (arrowStart && arrowPreview) {
      drawArrow(ctx, arrowStart, arrowPreview)
    }
  }, [actions, isDrawing, currentPoints, arrowStart, arrowPreview, activeTool])

  useEffect(() => {
    if (imageLoaded) redraw()
  }, [imageLoaded, redraw])

  // Coordinate helpers
  const getCanvasCoords = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current
    if (!canvas) return { x: 0, y: 0 }
    const rect = canvas.getBoundingClientRect()
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    }
  }, [])

  // Mouse handlers
  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const coords = getCanvasCoords(e)

    if (activeTool === 'freehand') {
      setIsDrawing(true)
      setCurrentPoints([coords])
    } else if (activeTool === 'arrow') {
      setIsDrawing(true)
      setArrowStart(coords)
      setArrowPreview(coords)
    } else if (activeTool === 'text') {
      setTextInput({ x: coords.x, y: coords.y, value: '' })
    }
  }, [activeTool, getCanvasCoords])

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return
    const coords = getCanvasCoords(e)

    if (activeTool === 'freehand') {
      setCurrentPoints(prev => [...prev, coords])
    } else if (activeTool === 'arrow') {
      setArrowPreview(coords)
    }
  }, [isDrawing, activeTool, getCanvasCoords])

  const handleMouseUp = useCallback(() => {
    if (!isDrawing) return

    if (activeTool === 'freehand' && currentPoints.length > 1) {
      setActions(prev => [...prev, { type: 'freehand', points: currentPoints }])
    } else if (activeTool === 'arrow' && arrowStart && arrowPreview) {
      setActions(prev => [...prev, { type: 'arrow', start: arrowStart, end: arrowPreview }])
    }

    setIsDrawing(false)
    setCurrentPoints([])
    setArrowStart(null)
    setArrowPreview(null)
  }, [isDrawing, activeTool, currentPoints, arrowStart, arrowPreview])

  // Text input commit
  const commitText = useCallback(() => {
    if (textInput && textInput.value.trim()) {
      setActions(prev => [
        ...prev,
        { type: 'text', position: { x: textInput.x, y: textInput.y }, content: textInput.value },
      ])
    }
    setTextInput(null)
  }, [textInput])

  const handleTextKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      commitText()
    } else if (e.key === 'Escape') {
      setTextInput(null)
    }
  }, [commitText])

  // Actions
  const handleUndo = useCallback(() => {
    setActions(prev => prev.slice(0, -1))
  }, [])

  const handleClear = useCallback(() => {
    setActions([])
  }, [])

  const handleDone = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    // Ensure final redraw before export
    redraw()
    // Small delay to ensure the canvas paint completes
    requestAnimationFrame(() => {
      const dataUrl = canvas.toDataURL('image/png')
      onSave(dataUrl)
    })
  }, [redraw, onSave])

  // Compute display size -- fit within container while preserving aspect ratio
  const img = imageRef.current
  const displayStyle: React.CSSProperties = img
    ? { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' as const }
    : {}

  const tools: Array<{ id: AnnotationTool; icon: typeof Pencil; label: string }> = [
    { id: 'freehand', icon: Pencil, label: 'Freehand' },
    { id: 'arrow', icon: ArrowRight, label: 'Arrow' },
    { id: 'text', icon: Type, label: 'Text' },
  ]

  return (
    <div className="flex flex-col h-full bg-ue-bg-darkest">
      {/* Toolbar */}
      <div className="flex items-center gap-1 p-1.5 border-b border-ue-border-medium bg-ue-bg-darker shrink-0">
        {tools.map(tool => (
          <button
            key={tool.id}
            onClick={() => setActiveTool(tool.id)}
            className={cn(
              'w-7 h-7 flex items-center justify-center rounded transition-colors',
              activeTool === tool.id
                ? 'bg-ue-accent-primary/20 text-ue-accent-primary'
                : 'text-ue-text-secondary hover:bg-white/10',
            )}
            title={tool.label}
          >
            <tool.icon size={14} />
          </button>
        ))}

        <div className="w-px h-5 bg-ue-border-medium mx-1" />

        <button
          onClick={handleUndo}
          disabled={actions.length === 0}
          className="w-7 h-7 flex items-center justify-center rounded text-ue-text-secondary hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          title="Undo"
        >
          <Undo2 size={14} />
        </button>
        <button
          onClick={handleClear}
          disabled={actions.length === 0}
          className="w-7 h-7 flex items-center justify-center rounded text-ue-text-secondary hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          title="Clear all"
        >
          <Trash2 size={14} />
        </button>

        <div className="flex-1" />

        <button
          onClick={onCancel}
          className="h-7 px-2 flex items-center gap-1 rounded text-ue-text-secondary hover:bg-white/10 transition-colors"
          style={{ fontSize: '11px' }}
        >
          <X size={12} />
          Cancel
        </button>
        <button
          onClick={handleDone}
          className="h-7 px-2 flex items-center gap-1 rounded bg-ue-accent-primary/20 text-ue-accent-primary hover:bg-ue-accent-primary/30 transition-colors"
          style={{ fontSize: '11px' }}
        >
          <Check size={12} />
          Done
        </button>
      </div>

      {/* Canvas area */}
      <div className="flex-1 relative overflow-hidden flex items-center justify-center p-2">
        {imageLoaded ? (
          <div className="relative" style={{ maxWidth: '100%', maxHeight: '100%' }}>
            <canvas
              ref={canvasRef}
              className="block cursor-crosshair"
              style={displayStyle}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            />

            {/* Floating text input */}
            {textInput && (
              <input
                type="text"
                autoFocus
                value={textInput.value}
                onChange={e => setTextInput(prev => prev ? { ...prev, value: e.target.value } : null)}
                onKeyDown={handleTextKeyDown}
                onBlur={commitText}
                className="absolute bg-transparent text-[#FF0000] border border-[#FF0000]/50 outline-none px-1"
                style={{
                  left: `${(textInput.x / (canvasRef.current?.width ?? 1)) * 100}%`,
                  top: `${(textInput.y / (canvasRef.current?.height ?? 1)) * 100}%`,
                  fontSize: `${FONT_SIZE}px`,
                  minWidth: '60px',
                }}
              />
            )}
          </div>
        ) : (
          <div className="text-ue-text-muted" style={{ fontSize: '11px' }}>
            Loading image...
          </div>
        )}
      </div>
    </div>
  )
}
